import { Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { ModalProps } from '@chakra-ui/react';
import { Box, chakra, Grid, Text, useTheme } from '@chakra-ui/react';

import { useFormatDate } from '@izimi/hooks';

import { CustomIcon } from '../icon';
import { BaseModal } from '.';

const Span = chakra('span');

export type DocumentSourcesModalSource = {
  name: string;
  date: string;
};

type Props = {
  sources: DocumentSourcesModalSource[];
  isOpen?: ModalProps['isOpen'];
  onClose?: ModalProps['onClose'];
};

const DocumentSourcesModal = ({ sources, isOpen, onClose }: Props) => {
  const { t, i18n } = useTranslation('shared');
  const formatDate = useFormatDate(i18n.language);
  const theme = useTheme();

  const isLast = (i: number) => sources.length - 1 === i;

  return (
    <BaseModal
      title={t('modals.documentSources.title')}
      isOpen={isOpen}
      onClose={onClose}
      RenderBody={
        <Grid gridTemplateColumns='20px 1fr' alignItems='center' columnGap={8}>
          {sources
            .slice()
            .reverse()
            .map((source, i) => (
              <Fragment key={source.date}>
                <CustomIcon gridColumn='1' mt={2} name={isLast(i) ? 'share' : 'copy'} />
                <Box gridRowStart='span 2' gridColumn='2'>
                  <Text variant='paragraph'>
                    <Trans
                      i18nKey={
                        isLast(i)
                          ? 'modals.documentSources.uploadedBy'
                          : 'modals.documentSources.copiedBy'
                      }
                      values={{
                        name: source.name,
                      }}
                      components={[<Span fontWeight='600' key='span' />]}
                      t={t}
                    />
                  </Text>
                  <Text variant='caption'>
                    {formatDate(new Date(source.date), 'dd/MM/yyyy')} {t('date.at')}{' '}
                    {formatDate(new Date(source.date), 'HH:mm')}
                  </Text>
                </Box>
                <Box
                  bg={`linear-gradient(${theme.colors.primary200} 18%, transparent 0%)`}
                  bgPos='60% 10%'
                  bgRepeat='repeat-y'
                  bgSize='2px 10px'
                  w='100%'
                  h='100%'
                  gridRow='span 2'
                  display={isLast(i) ? 'none' : ''}
                />
                <Box gridColumn='2' h={8} />
              </Fragment>
            ))}
        </Grid>
      }
    />
  );
};

export default DocumentSourcesModal;
